import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useLang, tx } from '../i18n/LanguageContext';

const MAX_TRIES = 20;

const Shell = ({ testId, Icon, tone, title, text, children }) => (
  <main data-testid={testId} className="pt-28 pb-20 min-h-[70vh] flex items-start justify-center px-4">
    <div className="w-full max-w-md rounded-3xl bg-white border border-slate-200 shadow-xl p-8 text-center">
      <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-5 ${tone}`}><Icon className="w-8 h-8" /></div>
      <h1 className="text-2xl font-black text-slate-900 mb-2">{title}</h1>
      <p className="text-slate-500 mb-7">{text}</p>
      {children}
    </div>
  </main>
);

const btn = 'inline-flex items-center justify-center h-12 px-6 rounded-full font-bold transition-[filter] hover:brightness-110';

export default function PaymentResult() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const sessionId = params.get('session_id');
  const cancelled = pathname.endsWith('/cancel');
  const [state, setState] = useState(cancelled ? 'cancelled' : sessionId ? 'checking' : 'error');

  useEffect(() => {
    if (cancelled || !sessionId) return undefined;
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') { await refreshUser?.(); setState('paid'); return; }
        if (['expired', 'failed'].includes(data.payment_status)) { setState('error'); return; }
      } catch (e) { /* keep polling */ }
      if (tries >= MAX_TRIES) { setState('timeout'); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, cancelled]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state === 'checking') return <Shell testId="payment-checking" Icon={Loader2} tone="bg-blue-50 text-blue-600 [&>svg]:animate-spin" title={L('جارٍ تأكيد الدفع...', 'Confirming your payment...')} text={L('لحظات من فضلك، لا تغلق الصفحة.', 'One moment please, keep this page open.')} />;
  if (state === 'paid') return (
    <Shell testId="payment-success" Icon={CheckCircle2} tone="bg-emerald-50 text-emerald-600" title={L('تم الدفع بنجاح!', 'Payment successful!')} text={L('تم تفعيل اشتراكك، ويمكنك الآن الوصول إلى كل الكورسات.', 'Your subscription is active. You now have access to every course.')}>
      <div className="flex flex-wrap justify-center gap-3">
        <Link to="/courses" data-testid="payment-go-courses" className={`${btn} bg-blue-600 text-white`}>{L('ابدأ التعلم', 'Start learning')}</Link>
        <Link to="/profile" data-testid="payment-go-profile" className={`${btn} border border-slate-200 text-slate-700`}>{L('حسابي', 'My profile')}</Link>
      </div>
    </Shell>
  );
  const isCancel = state === 'cancelled';
  return (
    <Shell testId={isCancel ? 'payment-cancelled' : 'payment-error'} Icon={XCircle} tone="bg-rose-50 text-rose-600"
      title={isCancel ? L('تم إلغاء الدفع', 'Payment cancelled') : state === 'timeout' ? L('لم يصلنا تأكيد الدفع بعد', 'Payment not confirmed yet') : L('لم يكتمل الدفع', 'Payment not completed')}
      text={isCancel ? L('لم يتم خصم أي مبلغ. يمكنك المحاولة مرة أخرى في أي وقت.', 'You were not charged. You can try again any time.') : state === 'timeout' ? L('إذا تم الخصم فسيتفعل اشتراكك خلال دقائق. تحقق من صفحة حسابك.', 'If you were charged, your plan will activate within minutes. Check your profile.') : L('حدث خطأ أثناء الدفع، حاول مرة أخرى.', 'Something went wrong with the payment, please try again.')}>
      <Link to="/subscription" data-testid="payment-back-plans" className={`${btn} bg-blue-600 text-white`}>{L('العودة إلى الباقات', 'Back to plans')}</Link>
    </Shell>
  );
}
